/**
 * The product page's issue model: every failing check of one product as a ranked issue with what
 * it costs, where it comes from and whether it blocks the verdict on its own. Builds on
 * `failedChecks` / `checkDetail` / `ruleLabel` so the wording matches the rest of the quality UI.
 */
import type { ProductScoreDetail } from '../types/quality-scores.types';

import {
  ISSUE_SOURCES,
  type IssueSource,
  type IssueSourceContext,
  issueSource,
} from './issue-source';
import { pointsLost } from './points-lost';
import { checkDetail, failedChecks, fieldRank, latestPhase } from './product-quality';
import { ruleLabel } from './quality-labels';

/** Which phase to read issues from: before enrichment, or after it (falls back to before). */
export type IssuePhase = 'pre' | 'post';

/** One failing check on one product, ready to list. */
export interface ProductIssue {
  field: string;
  checkId: string;
  /** The problem in words — "Description is too short". */
  words: string;
  /** "Found 234 chars · needs 500 or more", or null when the check carries neither. */
  measuredVsNeeded: string | null;
  severity: string;
  /** Overall points this issue costs (one decimal). */
  pointsLost: number;
  source: IssueSource;
  /** A critical failure fails the product at any score. */
  blocking: boolean;
}

const SEVERITY_RANK: Record<string, number> = { CRITICAL: 0, MAJOR: 1, MINOR: 2 };

/**
 * A product's failing checks as issues, costliest first (then most severe, then field order).
 * `post` reads the after-enrichment phase and falls back to before when the product wasn't
 * enriched; `pre` reads before only. The denominator is the fields scored in that phase.
 */
export function productIssues(
  detail: ProductScoreDetail,
  phase: IssuePhase = 'post',
  /** What is known about the product's feed, for the documented source overrides. */
  context: IssueSourceContext = {},
): ProductIssue[] {
  const scored = phase === 'pre' ? detail.pre : latestPhase(detail);
  if (!scored) return [];
  const scoredFields = scored.fields.filter((f) => f.score !== null).map((f) => f.field);
  return failedChecks(scored)
    .map(({ field, check }) => ({
      field,
      checkId: check.checkId,
      words: ruleLabel(check.checkId, field),
      measuredVsNeeded: checkDetail(check),
      severity: check.severity,
      pointsLost: pointsLost(check, field, scoredFields),
      source: issueSource(check.checkId, field, context),
      blocking: check.severity === 'CRITICAL',
    }))
    .sort(
      (a, b) =>
        b.pointsLost - a.pointsLost ||
        (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3) ||
        fieldRank(a.field) - fieldRank(b.field),
    );
}

/**
 * The one issue to fix first: a blocking one when there is any (it fails the product whatever
 * it costs), else the costliest. Null when the product has none.
 */
export function topIssue(issues: readonly ProductIssue[]): ProductIssue | null {
  return issues.find((issue) => issue.blocking) ?? issues[0] ?? null;
}

/** Issues gathered under one source, with their count and summed points. */
export interface IssueSourceGroup {
  source: IssueSource;
  issues: ProductIssue[];
  /** Summed points lost (one decimal). */
  pointsLost: number;
}

/**
 * Issues grouped by where they come from, in `ISSUE_SOURCES` order, every source present (empty
 * ones included so a summary row can say "none"). Works on one product's issues or many.
 */
export function issuesBySource(issues: readonly ProductIssue[]): IssueSourceGroup[] {
  return ISSUE_SOURCES.map((source) => {
    const inSource = issues.filter((issue) => issue.source === source);
    const points = inSource.reduce((sum, issue) => sum + issue.pointsLost, 0);
    return { source, issues: inSource, pointsLost: Math.round(points * 10) / 10 };
  });
}
